/*eslint max-lines-per-function: ["error", 180]*/
import React, { useMemo, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { ContractSearch } from '../../components/Contracts/ContractSearch';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { useNavigation } from '../../../common/navigation';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { ResponseError } from '../../../common/pokCore/autogenerated/pokApiClient';
import { WaitingModal } from '../../../common/components/Waiting/WaitingModal';

export const ContractsToSignView: React.FC = () => {
  const pok = usePokCore();
  const nav = useNavigation();
  const { t, tk } = useTranslation('projectLng');
  const [selectedContractsIds, setSelectedContractsIds] = useState<string[]>(
    [],
  );
  const [buttonTitle, setButtonTitle] = useState<string>(
    `${t(tk.contract.signAllContracts)}`,
  );
  const [waiting, setWaiting] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const getDataToSignByMe = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getToSignByMeByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, t, tk],
  );

  const getDataSignedByMe = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.contracts.getSignedByMeByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.contracts, t, tk],
  );

  const handleViewClick = async (id?: string, newWindow?: boolean) => {
    nav.contractEditor(id, newWindow);
  };

  const handleOnRowChooseClick = (key: string) => {
    let si: string[];

    if (!selectedContractsIds.includes(key)) {
      si = [...selectedContractsIds, key];
    } else {
      si = selectedContractsIds.filter(o => o !== key);
    }

    setButtonTitle(
      si.length > 0
        ? `${t(tk.contract.signContracts, { number: si.length })}`
        : `${t(tk.contract.signAllContracts)}`,
    );
    setSelectedContractsIds(si);
  };

  const signContracts = async () => {
    try {
      setWaiting(true);
      const result = await pok.contracts.sign(selectedContractsIds);
      if (result.errors && result.errors?.length > 0) {
        setShowSaveErrors(true);
        setSaveErrors(result.errors);
      } else {
        setShowSaveErrors(false);
      }
      setWaiting(false);
      setRefresh(!refresh);
      setSelectedContractsIds([]);
      setButtonTitle(`${t(tk.contract.signAllContracts)}`);

      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      setWaiting(false);
      setShowSaveErrors(true);
      setSaveErrors(await responseErrors(response as ResponseError));
    }
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.contract.contractsToSign)}
        </Card.Title>
        <TabsHistory
          activeKey="waiting_for_sign_contracts"
          id="waiting_for_sign_contracts"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="waiting_for_sign_contracts"
            title={t(tk.contract.waitingToSignFromMe)}
            className="m-3"
          >
            <ConfirmationButton
              variant="outline-primary"
              className="mb-4 me-1"
              onOK={() => signContracts()}
              confirmation={t(tk.contract.askSigningContracts)}
              isLoading={waiting}
            >
              {buttonTitle}
            </ConfirmationButton>
            <ValidationAlert
              show={showSaveErrors}
              errors={saveErrors}
              className="m-3"
            />
            {waiting && (
              <WaitingModal message={t(tk.contract.signingInProgress)} />
            )}
            {!waiting && (
              <ContractSearch
                getData={getDataToSignByMe}
                getCount={pok.contracts.getToSignByMeCount}
                enableMultiSelect={true}
                selectedKeys={selectedContractsIds}
                onRowClick={handleOnRowChooseClick}
                refresh={refresh}
                showNewWindowButton={true}
                withWaitingSignDate={true}
              />
            )}
          </Tab>
          <Tab
            eventKey="sign_by_me_contracts"
            title={t(tk.contract.signByMe)}
            className="m-3"
          >
            <ContractSearch
              getData={getDataSignedByMe}
              getCount={pok.contracts.getSignedByMeCount}
              onRowClick={handleViewClick}
              initialOrderDirection="DESC"
              displayStatus={true}
              withSignDate={true}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
